import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
  timelineItemClasses,
} from "@mui/lab";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Link,
  Paper,
  PaperProps,
  Stack,
  Typography
} from "@mui/material";
import axios, { AxiosError } from "axios";
import Loading from "components/Loading";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import advancedFormat from "dayjs/plugin/advancedFormat"
import { useAppSelector } from "hooks/redux";
import { useEffect, useState } from "react";
import Draggable from "react-draggable";
import { selectRfi } from "store/rfiSlice";
import RfiElement from "types/RfiElement";
import { RfiEditor, RfiHistory, RfiHistoryRecord } from "types/RfiTypes";

export function PaperComponent(props:PaperProps) {
  return (
    <Draggable
      handle="#history-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

interface IGovRfiHistoryModalProps {
  show: boolean;
  handleClose: ()=>void;
}

export const dateString = (date: string) => {
  const dateTime = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true,
    timeZoneName: "short",
  };

  const formattedDate = new Intl.DateTimeFormat("en-US", options).format(
    dateTime
  );
  return formattedDate;
};

const GovRfiHistoryModal = (props: IGovRfiHistoryModalProps) => {
  //hooks
  const rfi = useAppSelector(selectRfi);
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(advancedFormat);

  const timeZoneElement = rfi.finalize?.find((e: RfiElement) => {
    return e.mapsToField === "RESPONSES_DUE_TZ";
});

const timeZone = (timeZoneElement && timeZoneElement.answers && timeZoneElement.answers[0]?.value)
? timeZoneElement.answers[0]?.value : undefined;


  //States
  const [historyData, setHistoryData] = useState<RfiHistory | null | undefined>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);

  //Get history data
  useEffect(() => {
    if (rfi.rfi?.rfiId) {
      getHistoryData();
    }
  }, [rfi.rfi?.rfiId, rfi.rfi?.status]);

  const getHistoryData = async () => {
    try {
      setIsLoading(true);
      const result = await axios.get(`/api/v1/Rfi/${rfi.rfi?.rfiId}/history`);
      if (result.data) {
        setHistoryData(result.data);
      }
    } catch (error:  any) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadRfiPdf = async (record: RfiHistoryRecord) => {
    try {
      setSelectedRecord(record);
      setIsDownloading(true);
      await fetch(`/api/v1/rfi/print/${record.rfiId}`).then((response) => {
        response.blob().then((blob) => {
          let url = window.URL.createObjectURL(blob);
          let a = document.createElement("a");
          a.href = url;
          a.download = `${record.rfiTitle}_${record.versionNumber}.pdf`;
          a.rel = "noopener";
          a.click();
        });
      });
    } catch (error) {
      console.log(error);
    } finally {
      setSelectedRecord(null);
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Loading />
    )
  }
    return (
      <Dialog
        open={props.show}
        scroll="paper"
        maxWidth="md"
        aria-label="RFI History"
        aria-labelledby="history-dialog-title"
        tabIndex={-1}
        PaperComponent={PaperComponent}
      >
        <DialogTitle style={{ cursor: "move" }} id="history-dialog-title">
          <h1 className="h5">
            <i className="fa-solid fa-clock-rotate-left"></i> {`${rfi.rfi?.rfiNumber}`} History
          </h1>         
          <Typography variant={"subtitle1"} sx={{ fontStyle: "italic" }}>
            To view a prior version of this RFI, click the link for that
            version.
          </Typography>
        </DialogTitle>
        <DialogContent dividers={true}>
          <Box display="flex" height="100%">
            <Box flexGrow={2} height="100%">
              <Timeline
                sx={{
                  [`& .${timelineItemClasses.root}:before`]: {
                    flex: 0,
                    padding: 0
                  }
                }}
              >
                {historyData?.records?.map((record:RfiHistoryRecord, i:number) => {
                  let nameDisplay = ""
                  switch (record.action) {
                    case "RFI Created":
                    case "Draft Update In Progress":
                      nameDisplay = `Created by: ${record.customerName}`
                      break
                    case "RFI Posted to SAM":
                    case "Update Posted to SAM":
                      nameDisplay = `Posted by: ${record.customerName}`
                      break
                    case "Posting to SAM Failed":
                      nameDisplay = `Posting attempted by: ${record.customerName}`
                      break
                    case "Cancellation Posted to SAM":
                      nameDisplay = `Cancelled by: ${record.customerName}`
                      break
                  }
                  return (
                    <TimelineItem key={i}>
                      <TimelineSeparator>
                        <TimelineDot />
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent>
                        <Stack>
                          {(record.action === "RFI Posted to SAM" || record.action === "Update Posted to SAM") && (
                            <>
                              <Link href="#" onClick={() => downloadRfiPdf(record)}>
                                <span style={{ fontWeight: 600}}>{record.action}</span>
                                {record.versionNumber !== 0 && (
                                  <span style={{ fontWeight: 100}}>
                                    {` ( Amendment: 000${record.versionNumber} )`}
                                  </span>
                                )}
                              </Link>
                              {isDownloading && record === selectedRecord && (
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontStyle: "italic", fontWeight: 50 }}
                                >
                                  Downloading RFI...
                                </Typography>
                              )}
                            </>
                          )}
                          {(record.action !== "RFI Posted to SAM" && record.action !== "Update Posted to SAM") && (
                            <div>
                              <span style={{ fontWeight: 600}}>{record.action}</span>
                                {record.versionNumber !== 0 && (
                                  <span style={{ fontWeight: 100}}>
                                    {` ( Amendment: 000${record.versionNumber} )`}
                                  </span>
                                )}
                            </div>
                          )}
                          <Typography variant="subtitle1">
                            {record.actionDateTime
                              ? dayjs.utc(record.actionDateTime).tz(timeZone ?? "America/New_York").format("lll z")
                              : "(Date)"}
                          </Typography>
                          <Typography variant="subtitle1">
                            {nameDisplay}
                          </Typography>
                        </Stack>
                      </TimelineContent>
                    </TimelineItem>
                  )
                })}
                {!historyData?.records?.some((record:RfiHistoryRecord) => record.action === "RFI Created") && (
                  <TimelineItem>
                    <TimelineSeparator>
                      <TimelineDot />
                      <TimelineConnector />
                    </TimelineSeparator>
                    <TimelineContent>
                      <Stack>
                        <span style={{ fontWeight: 600}}>RFI Created</span>
                        <Typography variant="subtitle1">
                          {historyData?.dateCreated
                            ? dayjs.utc(historyData.dateCreated).tz(timeZone ?? "America/New_York").format("lll z")
                            : "(Date)"}
                        </Typography>
                        <Typography variant="subtitle1">
                          {rfi.rfi?.createdByName
                            ? `Created by: ${rfi.rfi?.createdByName}`
                            : "(Created By Name)"}
                        </Typography>
                      </Stack>
                    </TimelineContent>
                  </TimelineItem>
                )}
              </Timeline>
            </Box>
            <Divider
              className="p-2"
              flexItem
              sx={{ borderRightWidth: 2, marginRight:2 }}
              orientation="vertical"
            />
            <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%" gap={1} flexGrow={1}>
              <Box flexGrow={0}>
                <h5>Current Owner</h5>
                <div style={{ fontWeight: 600 }}>
                  {historyData?.ownerName || "(Current Owner)"}
                </div>
                <div className="row">
                  <div style={{ fontWeight: 100 }}>
                    {historyData?.ownerAssignedDate
                      ? `Assigned: ${dayjs.utc(historyData?.ownerAssignedDate).tz(timeZone??"America/New_York").format("lll z")}`
                      : `Assigned: (Date)`}
                  </div>
                </div>
                <div className="row">
                  <div style={{ fontWeight: 100 }}>
                    Assigned by:{" "}
                    {historyData?.ownerAssignedByName || "(Assigned By)"}
                  </div>
                </div>
              </Box>
              <Box flexGrow={1}>
                <Divider
                  sx={{ borderBottomWidth: 2, marginBottom:1 }}
                />
                <h5>Editors</h5>
                {(historyData?.editors?.length??0) > 0 && (
                  <div>
                    {historyData?.editors?.map((editor: RfiEditor, i:number) => {
                      return (
                        <div className="row mb-4" key={i}>
                          <div style={{ fontWeight: 600 }}>{editor.name ? editor.name : "(Name)"}</div>
                          <div style={{ fontWeight: 100 }}>
                            {editor.dateLastEdited
                              ? `Last edits: ${dayjs.utc(editor.dateLastEdited).tz(timeZone??"America/New_York").format("lll z")}`
                              : "Last edits: (Date)"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <button
            type="button"
            className="btn btn-primary k-mr-2"
            onClick={props.handleClose}
          >
            Close
          </button>
        </DialogActions>
      </Dialog>
    );
  
};

export default GovRfiHistoryModal;
